import React, { useRef } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { Download, ExternalLink, Printer, QrCode, X } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Per-agent "QR Link to Portal" modal.
 *
 * Each agent gets a personal QR that opens the buyer-portal sign-up/login page
 * tagged so any account created from it credits this agent. The same UTM scheme
 * is mirrored on the backend in
 * property_management/services/agent_referral.py — keep the source/medium values
 * in sync.
 *
 *   <portal>/login?utm_source=real_estate_agent&utm_medium=agent_qr
 *                 &utm_campaign=agent_<id>&agent_id=<id>
 *
 * Sign-ups flow through to the "Real Estate Agents" Traffic Sources row and its
 * per-agent drill-down (Analytics → Traffic Sources).
 */
const BUYER_PORTAL_BASE =
    process.env.REACT_APP_BUYER_PORTAL_URL || 'https://portal.collings.com.au';

// Must match property_management/services/agent_referral.py
const REAL_ESTATE_AGENT_SOURCE = 'real_estate_agent';
const AGENT_QR_MEDIUM = 'agent_qr';

export const buildPortalUrl = (agentId) => {
    const base = `${BUYER_PORTAL_BASE}/login`;
    if (!agentId) return base;
    const params = new URLSearchParams({
        utm_source: REAL_ESTATE_AGENT_SOURCE,
        utm_medium: AGENT_QR_MEDIUM,
        utm_campaign: `agent_${agentId}`,
        agent_id: String(agentId),
    });
    return `${base}?${params.toString()}`;
};

export default function AgentPortalQrModal({ agent, onClose }) {
    const canvasRef = useRef(null);
    const printSvgRef = useRef(null);

    const agentId = agent?.id || null;
    const agentName = agent?.name
        || `${agent?.first_name || ''} ${agent?.last_name || ''}`.trim()
        || agent?.email
        || 'Agent';
    const portalUrl = buildPortalUrl(agentId);

    const downloadPng = () => {
        const canvas = canvasRef.current?.querySelector('canvas');
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `portal-signup-qr-${agentId || 'agent'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(portalUrl);
            toast.success('Portal sign-up link copied.');
        } catch {
            toast.error('Could not copy.');
        }
    };

    const printSheet = () => {
        const svgEl = printSvgRef.current?.querySelector('svg');
        const qrSvgMarkup = svgEl
            ? svgEl.outerHTML.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ')
            : '';
        const logoUrl = `${window.location.origin}/collingslogo-removebg-preview.png`;
        const html = `<!doctype html><html><head><meta charset="utf-8" />
            <title>Sign up to the Collings Buyer Portal</title>
            <style>
                * { box-sizing: border-box; }
                body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 48px 40px;
                       color: #1f2937; text-align: center; }
                img.logo { height: 64px; margin-bottom: 28px; }
                h1 { font-size: 30px; margin: 0 0 8px; }
                h2 { font-size: 20px; font-weight: 600; color: #4b5563; margin: 0 0 36px; }
                .qr { display: inline-block; padding: 20px; border: 1px solid #e5e7eb; border-radius: 16px; }
                .agent { margin-top: 28px; font-size: 18px; font-weight: 700; }
                .hint { margin-top: 10px; font-size: 14px; color: #6b7280; }
                .url { margin-top: 6px; font-size: 12px; color: #9ca3af; word-break: break-all; }
            </style></head>
            <body>
                <img class="logo" src="${logoUrl}" alt="Collings" />
                <h1>Sign up to the Collings Buyer Portal</h1>
                <h2>Scan to browse listings, save favourites &amp; get matched</h2>
                <div class="qr">${qrSvgMarkup}</div>
                <div class="agent">${agentName}</div>
                <div class="hint">Point your phone camera at the code to get started.</div>
                <div class="url">${portalUrl}</div>
            </body></html>`;
        const w = window.open('', '_blank', 'width=900,height=1100');
        if (!w) {
            toast.error('Pop-up blocked — allow pop-ups to print the QR sheet.');
            return;
        }
        w.document.open();
        w.document.write(html);
        w.document.close();
        const trigger = () => { try { w.focus(); w.print(); } catch (_) { /* ignore */ } };
        const img = w.document.querySelector('img.logo');
        if (img && !img.complete) {
            img.addEventListener('load', trigger);
            img.addEventListener('error', trigger);
        } else {
            setTimeout(trigger, 250);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col my-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">QR Link to Portal</h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Your personal sign-up QR — {agentName}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="flex-shrink-0 -mr-1 p-1 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
                    <p className="text-xs text-gray-500 text-center">
                        Share or display this code to invite people to sign up to the buyer portal.
                        Every account created from it is credited to you in Analytics → Traffic Sources.
                    </p>

                    <div className="flex flex-col items-center bg-gray-50 rounded-xl p-5">
                        <div ref={canvasRef} className="bg-white p-4 rounded-lg border">
                            <QRCodeCanvas value={portalUrl} size={220} level="M" includeMargin={false} />
                        </div>
                        <div className="mt-3 text-[11px] text-gray-500 break-all text-center max-w-xs">{portalUrl}</div>
                        {!agentId && (
                            <div className="mt-2 text-[11px] text-amber-600 text-center max-w-xs">
                                Could not detect your agent ID — sign-ups from this code won't be
                                attributed. Please re-log in.
                            </div>
                        )}
                        <div className="mt-4 flex gap-2 flex-wrap justify-center">
                            <button type="button" onClick={printSheet}
                                className="text-xs flex items-center gap-1 px-3 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800">
                                <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
                            </button>
                            <button type="button" onClick={downloadPng}
                                className="text-xs flex items-center gap-1 px-3 py-2 border rounded-md bg-white hover:bg-slate-50">
                                <Download className="w-3.5 h-3.5" /> Download PNG
                            </button>
                            <button type="button" onClick={copyUrl}
                                className="text-xs flex items-center gap-1 px-3 py-2 border rounded-md bg-white hover:bg-slate-50">
                                Copy link
                            </button>
                            <a href={portalUrl} target="_blank" rel="noreferrer"
                                className="text-xs flex items-center gap-1 px-3 py-2 border rounded-md bg-white hover:bg-slate-50">
                                <ExternalLink className="w-3.5 h-3.5" /> Open portal
                            </a>
                        </div>
                    </div>

                    <div className="flex items-start gap-2 text-[11px] text-gray-400">
                        <QrCode className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>Tip: print it for your desk, add it to email signatures, or show it at open homes.</span>
                    </div>
                </div>

                {/* Off-screen SVG render — printSheet() pulls the markup out of this. */}
                <div ref={printSvgRef} style={{ position: 'absolute', left: -9999, top: -9999 }} aria-hidden>
                    <QRCodeSVG value={portalUrl} size={640} level="M" includeMargin={false} />
                </div>
            </div>
        </div>
    );
}
